// Resolves a Proma equipment discount to a dollar amount.
//
// Since the 2026-09 Proma change the discount can be either a flat amount or a
// percentage, distinguished by "Equipment Discount Type". The raw number alone
// is ambiguous — 20 means $20 under one type and 20% under the other — so every
// place that treats it as money must go through here.
//
// This mirrors resolveEquipDiscount in
// functions/sales_marketing_function/utils/customHelper.js. It is duplicated
// rather than imported because that module pulls in @sendgrid/mail, axios and
// promaHelper, none of which can be bundled for the browser.
//
// KEEP THE TWO IN SYNC.
// Accepts both the normalised string ("Percentage") and the raw Proma shape
// ([{ label: "Percentage" }]). promaHelper already unwraps `[0].label`, but a raw
// array reaching here would stringify to "[object Object]" and read as an AMOUNT —
// billing a 20% discount as $20. Cheap to tolerate, expensive to get wrong.
export const isPercentageDiscount = (type) => {
  const raw = Array.isArray(type) ? type[0]?.label : type;
  return String(raw).toLowerCase() === "percentage";
};

export const resolveEquipDiscount = (amount, type, equipmentTotal) => {
  const value = Number(amount) || 0;
  if (value <= 0) return 0;
  if (!isPercentageDiscount(type)) return value;
  const total = Number(equipmentTotal) || 0;
  // Round to cents so the figure on the agreement matches the one charged.
  return Math.round(total * value) / 100;
};

// True when Proma sent a percentage rather than a flat amount.
// How a discount should READ to a customer.
//
// The resolved dollar figure alone is not enough: "20% off" and "$20 off" both
// arrive as the number 20, and once resolved a 20% discount on a $597 setup shows
// as "$119.40" with no sign it was ever a percentage — so nobody reading the
// agreement can check the arithmetic or spot a wrong discount type.
//
// Percentage  -> "20% ($119.40)"     both the rate and what it came to
// Amount      -> "$340.00"
//
// Always two decimals. The old note interpolated the raw number, so a resolved
// 119.4 printed as "$119.4" on a document that quotes money everywhere else.
export const formatEquipDiscount = (amount, type, equipmentTotal) => {
  const resolved = resolveEquipDiscount(amount, type, equipmentTotal);
  const money = `$${resolved.toFixed(2)}`;
  if (!isPercentageDiscount(type)) return money;
  const pct = Number(amount) || 0;
  return `${pct}% (${money})`;
};
